import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import ModalContext from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { reportClientError } from 'services/errorReportingService'
import ConfirmDeleteChatModal from 'tripPlanner/components/Chat/ConfirmDeleteChatModal'
import { useRenameChatMutation } from 'tripPlanner/hooks/api/chat'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import { findClosestChat } from 'tripPlanner/assistant/utils/findClosestChat'
import { formatChatLabel } from 'tripPlanner/assistant/utils/format'
import { useAssistantHistory } from 'tripPlanner/assistant/hooks/useAssistantHistory'
import type { RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import { useRecentChatItems } from 'tripPlanner/pages/chat/useRecentChatItems'
import { useTripChatItineraryExport } from './useTripChatItineraryExport'

interface UseTripPlannerNavigationArgs {
  isLoggedIn: boolean
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  activeChatId?: number
  hasActiveChatItinerary?: boolean
}

export interface TripPlannerNavigationData {
  recentItems: Array<RecentChatItem>
  isRecentItemsLoading: boolean
  isRecentItemsError: boolean
  onRecentItemsRetry: () => void
  onChatRename: (chatId: number, name: string) => Promise<void>
  onChatDelete: (chatId: number) => Promise<void>
  onActiveChatDownloadPdf: (() => void) | undefined
  onActiveChatDownloadSpreadsheet: (() => void) | undefined
}

/** History, rename, delete and export for the nav, shared by the side nav and the menu drawer. */
export function useTripPlannerNavigation({
  isLoggedIn,
  onChatDeleted,
  activeChatId,
  hasActiveChatItinerary = false,
}: UseTripPlannerNavigationArgs): TripPlannerNavigationData {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { chatHistory, refetchChatList, deleteChatMutation } = useAssistantHistory({ isLoggedIn })
  const recentItems = useRecentChatItems({ isLoggedIn })
  const { mutateAsync: renameChatMutation } = useRenameChatMutation()
  const { downloadPdf, downloadSpreadsheet } = useTripChatItineraryExport({
    chatId: hasActiveChatItinerary ? activeChatId : undefined,
  })

  const getChatDisplayName = useCallback((chat: TripPlannerChat | undefined) => {
    if (!chat) {
      return intl.formatMessage({
        id: '-LmD19gmaBgkOCUWrnKU',
        defaultMessage: 'this chat',
        description: 'Fallback display name for a trip planner chat when no chat is found',
      })
    }
    const trimmedName = chat.name?.trim()
    if (trimmedName) {
      return trimmedName
    }
    return formatChatLabel(getChatLastActivityTimestamp(chat), intl)
  }, [intl])

  const onChatDelete = useCallback(async(chatIdToDelete: number) => {
    const chatToDelete = chatHistory.find((chat) => chat.id === chatIdToDelete)
    const chatName = getChatDisplayName(chatToDelete)

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={chatName} />,
    )

    if (!confirmed) {
      return
    }

    const fallbackChatId = findClosestChat(chatHistory, chatIdToDelete)

    try {
      await deleteChatMutation({ chatId: chatIdToDelete })
      await refetchChatList()
      onChatDeleted(chatIdToDelete, fallbackChatId)
    } catch(error) {
      reportClientError(error, { message: 'Failed to delete trip planner chat', chatId: chatIdToDelete })
      showSnackbar(intl.formatMessage({
        id: 'GjRGiJeZErGMMw8bQjrE',
        defaultMessage: 'Unable to delete chat. Please try again.',
        description: 'Error snackbar message when chat deletion fails in the trip planner AI chat',
      }), 'critical')
    }
  }, [chatHistory, deleteChatMutation, getChatDisplayName, intl, onChatDeleted, refetchChatList, showModal])

  const onChatRename = useCallback(async(chatIdToRename: number, name: string) => {
    try {
      await renameChatMutation({ chatId: chatIdToRename, name })
      await refetchChatList()
    } catch(error) {
      reportClientError(error, { message: 'Failed to rename trip planner chat', chatId: chatIdToRename })
      showSnackbar(intl.formatMessage({
        id: 'tvKzJmAgeic_SZz_ioDb',
        defaultMessage: 'Unable to rename chat. Please try again.',
        description: 'Error snackbar message when chat rename fails in the trip planner AI chat',
      }), 'critical')
      throw error
    }
  }, [intl, refetchChatList, renameChatMutation])

  return {
    recentItems: recentItems.items,
    isRecentItemsLoading: recentItems.isLoading,
    isRecentItemsError: recentItems.isError,
    onRecentItemsRetry: recentItems.refetch,
    onChatRename,
    onChatDelete,
    onActiveChatDownloadPdf: hasActiveChatItinerary ? downloadPdf : undefined,
    onActiveChatDownloadSpreadsheet: hasActiveChatItinerary ? downloadSpreadsheet : undefined,
  }
}
